import { Link } from "react-router-dom";
import { Mail, Instagram, Youtube } from "lucide-react";
import logoDark from "@/assets/logo-dark.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-card text-foreground">
      <div className="border-t border-divider" />
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <img src={logoDark} alt="Nail Point Records" className="h-[17rem] w-auto mb-3" />
            <p className="text-muted-foreground text-sm mb-4 max-w-md">
              NailPoint Records is a creative house building Christian artists with conviction, craft, and cultural impact.
            </p>

            {/* Social & Streaming Icons */}
            <div className="flex items-center gap-4 mb-4">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent transition-colors duration-300">
                <Instagram className="h-5 w-5" strokeWidth={1.5} />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent transition-colors duration-300">
                <Youtube className="h-5 w-5" strokeWidth={1.5} />
              </a>
              {/* Spotify SVG icon */}
              <a href="https://open.spotify.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent transition-colors duration-300">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
              </a>
              {/* Apple Music SVG icon */}
              <a href="https://music.apple.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent transition-colors duration-300">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043A5.022 5.022 0 0019.7.165a10.798 10.798 0 00-1.564-.132C17.498.005 16.86 0 14.952 0H9.047c-1.908 0-2.546.005-3.184.033a10.798 10.798 0 00-1.564.132A5.022 5.022 0 002.425.891C1.307 1.624.562 2.624.245 3.934a9.23 9.23 0 00-.24 2.19C.002 6.762 0 7.4 0 9.047v5.906c0 1.647.002 2.285.005 2.923a9.23 9.23 0 00.24 2.19c.317 1.31 1.062 2.31 2.18 3.043a5.022 5.022 0 001.874.726c.5.105 1.027.148 1.564.132C6.501 23.995 7.139 24 9.047 24h5.906c1.908 0 2.546-.005 3.184-.033a10.798 10.798 0 001.564-.132 5.022 5.022 0 001.874-.726c1.118-.733 1.863-1.733 2.18-3.043a9.23 9.23 0 00.24-2.19c.003-.638.005-1.276.005-2.923V9.047c0-1.647-.002-2.285-.005-2.923zM17.09 17.505c0 .554-.449.961-.99.961a1.028 1.028 0 01-.12-.007H7.02a1.028 1.028 0 01-.12.007c-.541 0-.99-.407-.99-.961V6.495c0-.554.449-.961.99-.961.04 0 .08.002.12.007h8.96c.04-.005.08-.007.12-.007.541 0 .99.407.99.961v11.01zM8.89 8.877v5.882a.96.96 0 01-.655.903 2.052 2.052 0 00-.101.037c-.383.15-.774.236-1.097.236-.705 0-1.157-.39-1.157-1.004 0-.627.536-1.117 1.22-1.117.253 0 .506.058.746.169l.044.02V9.944L13.11 8.56v6.48a.96.96 0 01-.655.903 2.052 2.052 0 00-.101.037c-.383.15-.774.236-1.097.236-.705 0-1.157-.39-1.157-1.004 0-.627.536-1.117 1.22-1.117.253 0 .506.058.746.169l.044.02V8.188L8.89 8.877z"/></svg>
              </a>
            </div>

            <a href="mailto:info@nailpointrecords.com" className="flex items-center gap-2 text-muted-foreground hover:text-accent transition-colors duration-300 text-sm">
              <Mail className="h-4 w-4" />
              info@nailpointrecords.com
            </a>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-sm uppercase tracking-[0.08em]">Navigate</h4>
            <ul className="space-y-2">
              {[
                { to: "/artists", label: "Artists" },
                { to: "/services", label: "Services" },
                { to: "/mastering", label: "Mastering" },
                { to: "/theology", label: "Theology" },
                { to: "/blog", label: "Blog" },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-muted-foreground hover:text-accent transition-colors duration-300 text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Get Involved */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-sm uppercase tracking-[0.08em]">Get Involved</h4>
            <ul className="space-y-2">
              {[
                { to: "/apply", label: "Apply as Artist" },
                { to: "/contact", label: "Contact Us" },
                { to: "/about", label: "About" },
                { to: "/portal", label: "Artist Portal" },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-muted-foreground hover:text-accent transition-colors duration-300 text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-divider">
        <div className="container mx-auto px-4 py-4 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p className="flex items-center gap-2">
            <span className="text-accent/60 font-heading text-xs tracking-[0.15em]">Α — Ω</span>
            <span className="text-divider">·</span>
            © {currentYear} Nail Point Records
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-accent transition-colors duration-300">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-accent transition-colors duration-300">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
